import React, { Component, createRef } from "react";
import styled from "styled-components";
import i18n from "i18next";

import TitleWithTime from "./titleWithTime";
import TitleWithIcon from "./titleWithIcon";
import ListWithBgIcon from "./listWithBgIcon";
import TextWithIcon from "./textWithIcon";
import IconInColumnAndText from "./iconInColumnAndText";
import IconColumnsAndText from "./iconColumnsAndText";
import { Icon } from "../../../UIElements/icons.js";
import { colors, breakpoints } from "../../../UIGlobals";
import { hexToRgba } from "../../../helpers/colors";
import { AppConfig, Theme } from "../../../appConfig";
import { CenteredSlide, ContactSlide, DualContentSlide, IntroSlide, ModuleDividerSlide, OpenQuestion, SlidesWrapper } from "./componentsV2";
import { Breakpoint, IconButton, IconButtonSize, IconButtonVariant, IconName } from "@elearning-platform/ui";
import { AppZIndex } from "../../../enums";

const TimerSlidesDiv = styled.div`
  position: relative;
  margin-bottom: 80px;
`;

const ButtonsWrapper = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  z-index: ${AppZIndex.TimerSlideButtons};
`;

const ModernButtonsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  position: ${props => props.$stuckButtons ? "fixed" : "absolute"};
  bottom: ${props => props.$stuckButtons ? "72px" : "20px"};
  right: 20px;
  transform: ${props => props.$stuckButtons ? "translateY(-50%)" : "none"};
  z-index: ${AppZIndex.TimerSlideButtons};

  @media ${Breakpoint.SmallDesktop} {
    position: absolute;
    right: 20px;
    bottom: 20px;
  }
`;

const StyledIconButton = styled(IconButton)`
  &:focus {
    box-shadow: none;
  }
`;

const NavButton = styled.button`
  width: ${props => props.nextChapter ? "200px" : "50px"};
  height: 50px;
  background: transparent;
  border-radius: 25px;
  border: 2px solid ${props => props.nextChapter
    ? props.mainColor
    : colors().gray1};
  margin-left: 7px;
  color: ${props => props.mainColor};
  font-size: 14px;
  cursor: pointer;
  &:disabled {
    cursor: default;
    opacity: 0.85;
  }
`;

const TimerContainer = styled.div`
  position: absolute;
  top: 0;
  right: 20px;
  display: flex;
  align-items: center;
  @media (${breakpoints().maxM}) {
    margin-top: 8px;
  }
`;

const TimerMessage = styled.span`
  color: ${props => props.darkBg ? colors().white : colors().deepGray};
  font-size: 14px;
  line-height: 40px;
  margin-left: 10px;
  z-index: 2;
  @media (${breakpoints().maxM}) {
    font-size: 11px;
  }
`;

const Timer = styled(TimerMessage)`
  font-weight: bold;
  margin: 0;
`;

const IconText = styled.span`
  margin-right: 10px;
`;

class TimerSlides extends Component {
  state = {
    loading: true,
    activeInteraction: 0,
    activePart: 0,
    stuckButtons: false,
  };
  timeout = false;

  slideRef = createRef();
  modernButtonsRef = createRef();

  componentDidMount() {
    if (this.props.baseUrl + this.props.config) {
      fetch(this.props.baseUrl + this.props.config, { credentials: 'include' })
        .then(data => data.json())
        .then(data => this.setState({ ...data }, this.setState({ loading: false })));
    }
    window.addEventListener("scroll", this.handleScroll);
    if (!this.timeout) {
      this.showTimeout();
    }
  }

  componentWillUnmount() {
    if (this.timeout) {
      clearInterval(this.timeout);
    }
    this.timeout = false;
    window.removeEventListener("scroll", this.handleScroll);
  }

  handleScroll = () => {
    const slide = this.slideRef.current;
    if (!slide || window.innerWidth > 1024) {
      return;
    }

    const rect = slide.getBoundingClientRect();

    const bottomBarHeight = 72;
    const isBottomVisible = rect.bottom <= (window.innerHeight - bottomBarHeight);
    if (isBottomVisible) {
      this.setState({ stuckButtons: false });
    } else {
      this.setState({ stuckButtons: true });
    }
  }

  withModernWrapper(children) {
    const { activeInteraction, interactions } = this.state;
    const partTitle = interactions[activeInteraction].partTitle;

    return (
      <SlidesWrapper moduleNumber={this.props.currentSlideIndex} partTitle={partTitle} slideTitle={this.props.subtitle}>
        {children}
      </SlidesWrapper>
    )
  }

  renderInteraction(interaction) {
    const { activeInteraction, activePart, interactions } = this.state;
    const data = interactions[activeInteraction].parts[activePart];
    data.iconBaseUrl = `${this.props.baseUrl}icons/`;
    data.fileBaseUrl = `${AppConfig.content}/assets/files/`;

    switch (interaction) {
    case "titleWithTime":
      return <TitleWithTime data={data} />;
    case "titleWithIcon":
      return <TitleWithIcon data={data} />;
    case "listWithBgIcon":
      return <ListWithBgIcon data={data} />;
    case "textWithBgIcon":
      return (
        <TextWithIcon
          data={data}
          activePart={activePart}
          parts={interactions[activeInteraction].parts.length}
          bigIcon
        />
      );
    case "textWithIcon":
      return (
        <TextWithIcon
          data={data}
          activePart={activePart}
          parts={interactions[activeInteraction].parts.length}
        />
      );
    case "textWithIconReversed":
      return (
        <TextWithIcon
          data={data}
          activePart={activePart}
          parts={interactions[activeInteraction].parts.length}
          bigIcon
          reversed
        />
      );
    case "textColumn":
      return (
        <TextWithIcon
          data={data}
          activePart={activePart}
          parts={interactions[activeInteraction].parts.length}
          textColumnType
        />
      );
    case "iconInColumnAndText":
      return (
        <IconInColumnAndText
          data={data}
          activePart={activePart}
          parts={interactions[activeInteraction].parts.length}
        />
      );
    case "iconInColumnAndTextLight":
      return (
        <IconInColumnAndText
          data={data}
          activePart={activePart}
          parts={interactions[activeInteraction].parts.length}
          columnColor={colors().dirtyWhite}
          lightTheme
        />
      );
    case "iconInColumnAndTextWhite":
      return (
        <IconInColumnAndText
          data={data}
          activePart={activePart}
          parts={interactions[activeInteraction].parts.length}
          columnColor={colors().white}
          lightTheme
        />
      );
    case "iconInColumnAndTextColor":
      return (
        <IconInColumnAndText
          data={data}
          activePart={activePart}
          parts={interactions[activeInteraction].parts.length}
          columnColor={colors().maroon}
        />
      );
    case "iconInColumnAndIconList":
      const getColumnColor = () => {
        switch (data.columnBgTheme) {
          case "darkBlue":
            return colors().darkBlue;
          default:
            return undefined;
        }
      };

      return (
        <IconInColumnAndText
          data={data}
          activePart={activePart}
          parts={interactions[activeInteraction].parts.length}
          iconList
          columnColor={getColumnColor()}
        />
      );
    case "iconInColumnAndIconListWhite":
      return (
        <IconInColumnAndText
          data={data}
          activePart={activePart}
          parts={interactions[activeInteraction].parts.length}
          columnColor={colors().white}
          lightTheme
          iconList
        />
      );
    case "iconInColumnAndIconListLight":
      return (
        <IconInColumnAndText
          data={data}
          activePart={activePart}
          parts={interactions[activeInteraction].parts.length}
          columnColor={colors().dirtyWhite}
          lightTheme
          iconList
        />
      );
    case "iconInColumnAndIconListCounter":
      return (
        <IconInColumnAndText
          data={data}
          activePart={activePart}
          parts={interactions[activeInteraction].parts.length}
          iconList
          counterList
        />
      );
    case "iconInColumnAndIconListCounterWhite":
      return (
        <IconInColumnAndText
          data={data}
          activePart={activePart}
          parts={interactions[activeInteraction].parts.length}
          columnColor={colors().white}
          iconList
          counterList
          lightTheme
        />
      );
    case "iconInColumnAndMap":
      return (
        <IconInColumnAndText
          data={data}
          activePart={activePart}
          parts={interactions[activeInteraction].parts.length}
          columnColor={colors().dirtyWhite}
          lightTheme
          mapType
        />
      );
    case "iconColumnsAndText":
      return <IconColumnsAndText data={data} hasTextColumn />;
    case "iconColumns":
      return <IconColumnsAndText data={data} />;
    case "modernIntroSlide":
      return (
        <IntroSlide
          data={data}
          onButtonClick={(activeInteraction === interactions.length - 1 && activePart === interactions[activeInteraction].parts.length - 1)
            ? this.props.callComplete
            : () => this.goNext()
          }
        />
      );
    case "modernModuleDividerSlide":
      return <ModuleDividerSlide baseUrl={this.props.baseUrl} data={data} moduleNumber={this.props.currentSlideIndex} />
    case "modernDualContentSlide":
      return (
        this.withModernWrapper(
          <DualContentSlide
            activePart={activePart}
            baseUrl={this.props.baseUrl}
            data={data}
            partsCount={interactions[activeInteraction].parts.length}
          />
        )
      );
    case "modernContactSlide":
      return <ContactSlide baseUrl={this.props.baseUrl} data={data} />
    case "modernCenteredSlide":
      return this.withModernWrapper(<CenteredSlide baseUrl={this.props.baseUrl} data={data} />);
    case "openQuestion":
      return (
        <OpenQuestion
          data={data}
          onButtonClick={(activeInteraction === interactions.length - 1 && activePart === interactions[activeInteraction].parts.length - 1)
            ? this.props.callComplete
            : () => this.goNext()
          }
        />
      );
    default:
      return <div>{i18n.t("interactions.external.external.lessonDownloadError")}</div>;
    }
  }

  goNext() {
    const { activeInteraction, activePart, interactions } = this.state;

    this.setState({
      activePart: activePart >= interactions[activeInteraction].parts.length - 1
        ? 0
        : activePart + 1,
      activeInteraction: activePart >= interactions[activeInteraction].parts.length - 1
        ? activeInteraction + 1
        : activeInteraction
    });
  }

  goPrev() {
    const { activeInteraction, activePart, interactions } = this.state;

    this.setState({
      activePart: activePart <= 0
        ? interactions[activeInteraction - 1].parts.length - 1
        : activePart - 1,
      activeInteraction: activePart <= 0
        ? activeInteraction - 1
        : activeInteraction
    });
  }

  showTimeout() {
    this.timeout = setInterval(() => {
      const timer = this.state.timer - 1;
      this.setState({ ...this.state, timer });
      if (timer === 0) {
        clearInterval(this.timeout);
      }
    }, 1000);
  }

  render() {
    const { loading, activeInteraction, activePart, interactions } = this.state;
    const data = interactions && interactions[activeInteraction].parts[activePart];
    const activeInteractionType = interactions ? interactions[activeInteraction].parts[activePart].type : "";
    const darkBg = interactions
      && (activeInteractionType === "titleWithIcon"
      || activeInteractionType === "titleWithTime");
    const getMainColor = () => {
      if (data) {
        switch (data.detailsTheme) {
          case "darkBlue":
            return colors().darkBlue;
          case "whiteSoft":
            return hexToRgba(colors().white, 0.25);
          default:
            return colors().maroon;
        }
      }

      return colors().maroon;
    };

    return (
      loading ? i18n.t("lesson.slide.slide.loading") :
        <TimerSlidesDiv ref={this.slideRef}>
          <TimerContainer>
            {this.timeout && (
              this.state.timer > 0 ? (
                <>
                  <Icon icon="clock" size={13} width={13} color={colors().gray1} />
                  <TimerMessage darkBg={darkBg}>
                    {i18n.t("interactions.external.external.skipChapterFor")}
                  </TimerMessage>
                  <span>&nbsp;</span>
                  <Timer darkBg={darkBg}>
                    {`${this.state.timer} ${i18n.t("interactions.external.external.timerUnit")}`}
                  </Timer>
                </>
              ) : (
                <TimerMessage darkBg={darkBg}>
                  {i18n.t("interactions.external.external.skipChapter")}
                </TimerMessage>
              )
            )}
          </TimerContainer>
          {interactions && this.renderInteraction(activeInteractionType)}
          {interactions && activeInteractionType !== "openQuestion" && !Theme.ModernInteractions ? (
            <ButtonsWrapper>
              <NavButton
                onClick={() => this.goPrev()}
                disabled={activeInteraction <= 0 && activePart <= 0}
              >
                <Icon icon="interaction_arrow_left" size={14} width={14} color={colors().gray1} />
              </NavButton>
              {(activeInteraction < interactions.length - 1 || activePart < interactions[activeInteraction].parts.length - 1) &&
                <NavButton
                  onClick={() => this.goNext()}
                >
                  <Icon icon="interaction_arrow_right" size={14} width={14} color={colors().gray1} />
                </NavButton>
              }
              {(activeInteraction === interactions.length - 1 && activePart === interactions[activeInteraction].parts.length - 1) &&
                <NavButton
                  mainColor={getMainColor()}
                  nextChapter
                  onClick={this.props.callComplete}
                  disabled={this.state.timer > 0}
                >
                  <IconText>{i18n.t("interactions.external.external.nextChapter")}</IconText>
                  <Icon icon="interaction_arrow_right" size={14} width={14} color={getMainColor()} />
                </NavButton>
              }
            </ButtonsWrapper>
          ) : !["modernIntroSlide", "openQuestion"].includes(activeInteractionType) && (
            <ModernButtonsWrapper $stuckButtons={this.state.stuckButtons} ref={this.modernButtonsRef}>
              <StyledIconButton
                disabled={activeInteraction === 0 && activePart === 0}
                icon={IconName.ChevronLeft}
                onClick={() => this.goPrev()}
                size={IconButtonSize.Small}
                variant={IconButtonVariant.PrimaryWhite}
              />
              <StyledIconButton
                disabled={(this.state.timer > 0)
                  && (activeInteraction === interactions.length - 1)
                  && (activePart === interactions[activeInteraction].parts.length - 1)
                }
                icon={IconName.ChevronRight}
                onClick={
                  (activeInteraction === interactions.length - 1 && activePart === interactions[activeInteraction].parts.length - 1)
                    ? this.props.callComplete
                    : () => this.goNext()
                  }
                size={IconButtonSize.Small}
                variant={IconButtonVariant.PrimaryWhite}
              />
            </ModernButtonsWrapper>
          )}
        </TimerSlidesDiv>
    );
  }
}

export default TimerSlides;
